import { i18n } from "@/i18n";
import { type RemovableRef, useStorage } from "@vueuse/core";
import type { UnwrapRef } from "vue";

export function generateRandomId(length = 5) {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let result = "";
  for (let index = 0; index < length; index++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

function getUseStorage<T>(
  key: string,
  shouldParse = false,
  storage: Storage,
  defaultVal: T | null = null // Undefined is broken (creates invalid value in store)
): Ref<T | UnwrapRef<T> | string | null | undefined> {
  const state = useStorage(key, defaultVal, storage);
  if (!shouldParse) return state;

  try {
    return ref(JSON.parse(state.value as string));
  } catch {
    console.error("Error loading key", key);
    return ref(defaultVal);
  }
}

export function getLocalStorageReac<T>(key: string, shouldParse = false, defaultVal?: T) {
  return getUseStorage(key, shouldParse, localStorage, defaultVal);
}

export function getSessionStorageReac<T>(key: string, shouldParse = false, defaultVal?: T) {
  return getUseStorage(key, shouldParse, sessionStorage, defaultVal);
}

export function setLocalStorageReac(key: string, value: any): RemovableRef<any> {
  let tempValue = value;
  if (typeof value !== "string") {
    tempValue = JSON.stringify(value);
  }

  // GH: https://github.com/vueuse/vueuse/issues/2193
  const state = useStorage(key, null, localStorage);
  state.value = tempValue;

  if (localStorage[key] !== tempValue) {
    localStorage[key] = tempValue;
    return ref(tempValue);
  }
  return state;
}

export function setSessionStorageReac(key: string, value: any): RemovableRef<any> {
  let tempValue = value;
  if (typeof value !== "string") {
    tempValue = JSON.stringify(value);
  }

  // GH: https://github.com/vueuse/vueuse/issues/2193
  const state = useStorage(key, null, sessionStorage);
  state.value = tempValue;

  if (sessionStorage[key] !== tempValue) {
    sessionStorage[key] = tempValue;
    return ref(tempValue);
  }
  return state;
}

export const EMAIL_REGEX =
  /(?:[a-z\d!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z\d!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\u0001-\u0008\u000B\u000C\u000E-\u001F!\u0023-\u005B\u005D-\u007F]|\\[\u0001-\u0009\u000B\u000C\u000E-\u007F])*")@(?:(?:[a-z\d](?:[a-z\d-]*[a-z\d])?\.)+[a-z\d](?:[a-z\d-]*[a-z\d])?|\[(?:(?:25[0-5]|2[0-4]\d|[01]?\d{1,2})\.){3}(?:25[0-5]|2[0-4]\d|[01]?\d{1,2}|[a-z\d-]*[a-z\d]:(?:[\u0001-\u0008\u000B\u000C\u000E-\u001F\u0021-\u005A\u0053-\u007F]|\\[\u0001-\u0009\u000B\u000C\u000E-\u007F])+)])/;

export function $te(key: string): string {
  return i18n.global.t(key, {}, { locale: "sr" });
}
